import { ACCOUNT_SWITCH_HINT } from './public-notice'

export type QueueStatus = 'accepted' | 'processing' | 'completed' | 'review' | 'failed' | 'unused'

export interface RedeemQueueItem {
  id: string
  jobId?: string
  code: string
  plan: string
  email: string
  submittedAt: number
  redemptionToken: string
  status: QueueStatus
  message: string
  updatedAt: number
  hasSession?: boolean
}

export const QUEUE_KEY = 'cdk_redeem_queue_v1'
export const PROGRESS_KEY = 'cdk_redeem_progress_v1'
const MAX_ITEMS = 20

export function loadQueue(): RedeemQueueItem[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY)
    if (!raw) return []
    const list = JSON.parse(raw)
    return Array.isArray(list) ? list : []
  } catch {
    return []
  }
}

export function saveQueue(items: RedeemQueueItem[]) {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(items.slice(0, MAX_ITEMS)))
}

export function clearRedeemClientCache() {
  try {
    localStorage.removeItem(QUEUE_KEY)
  } catch {
    /* ignore quota / private mode */
  }
  try {
    sessionStorage.removeItem(PROGRESS_KEY)
  } catch {
    /* ignore quota / private mode */
  }
}

export function pushQueue(item: Omit<RedeemQueueItem, 'id' | 'updatedAt' | 'submittedAt'> & {
  submittedAt?: number
}) {
  const now = Date.now()
  const next: RedeemQueueItem = {
    ...item,
    id: `${now}-${Math.random().toString(36).slice(2, 8)}`,
    submittedAt: item.submittedAt || now,
    updatedAt: now,
  }
  const list = loadQueue().filter((x) => x.code !== next.code && x.redemptionToken !== next.redemptionToken)
  saveQueue([next, ...list])
  return next
}

export function updateQueue(id: string, patch: Partial<RedeemQueueItem>) {
  const list = loadQueue().map((x) => (x.id === id ? { ...x, ...patch, updatedAt: Date.now() } : x))
  saveQueue(list)
  return list
}

export function findQueueByCode(code: string) {
  const key = String(code || '').trim().toUpperCase()
  if (!key) return null
  return loadQueue().find((x) => x.code.trim().toUpperCase() === key) || null
}

export function formatQueueTime(ts: number) {
  if (!ts) return ''
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return ''
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
}

export function parseTimeValue(value: unknown) {
  if (value == null || value === '') return ''
  if (typeof value === 'number' && value > 0) return formatQueueTime(value)
  const raw = String(value).trim().replace('T', ' ').replace(/,/g, '-')
  const ms = Date.parse(raw)
  if (!Number.isNaN(ms)) return formatQueueTime(ms)
  return raw.length >= 16 ? raw.slice(0, 19) : raw
}

export function mapQueueStatus(st: string, httpAccepted = false): QueueStatus {
  const s = String(st || '').toLowerCase()
  if (s === 'completed' || s === 'used') return 'completed'
  if (['failed', 'declined', 'cancelled', 'failed_precharge', 'already_satisfied', 'account_busy', 'error'].includes(s)) return 'failed'
  if (s === 'review' || s === 'pending' || s === 'manual_review' || s === 'reconciling') return 'review'
  if (s === 'accepted') return 'accepted'
  if (s === 'unused') return 'unused'
  if (s === 'queued' || s === 'running' || s === 'processing' || s === 'requires_action' || s === 'plus_paid' || s === 'funding_pending' || s === 'awaiting_card' || s === 'dispatching' || s === '' || httpAccepted) return 'processing'
  return 'processing'
}

export function statusLabel(st: QueueStatus | string) {
  const s = String(st || '').toLowerCase()
  if (s === 'completed' || s === 'used') return '已开通'
  if (s === 'review' || s === 'pending' || s === 'manual_review' || s === 'reconciling') return '待对账'
  if (s === 'failed' || s === 'declined' || s === 'cancelled' || s === 'failed_precharge' || s === 'already_satisfied' || s === 'account_busy') return '失败'
  if (s === 'processing' || s === 'running' || s === 'queued' || s === 'requires_action' || s === 'plus_paid' || s === 'funding_pending' || s === 'awaiting_card' || s === 'dispatching') return '处理中'
  if (s === 'accepted') return '已受理'
  if (s === 'unused') return '未使用'
  return st || '—'
}

export function isOpenQueueStatus(st: string) {
  const s = mapQueueStatus(st)
  return s === 'processing' || s === 'accepted' || s === 'review'
}

/** 空状态或弱状态不得盖掉已失败/已完成。返回 null 表示不要改 status。 */
export function resolveQueueWriteStatus(
  current: string | undefined,
  incoming: string,
  opts?: { inFlight?: boolean },
): QueueStatus | null {
  const raw = String(incoming || '').trim()
  if (!raw) return null
  let next = mapQueueStatus(incoming)
  const cur = mapQueueStatus(current || '')
  if (next === 'unused' && opts?.inFlight && current && isOpenQueueStatus(current)) {
    next = 'processing'
  }
  if ((cur === 'failed' || cur === 'completed') && (next === 'processing' || next === 'unused' || next === 'accepted')) {
    return null
  }
  return next
}

export function normalizeAccountKey(email: string) {
  return String(email || '').trim().toLowerCase()
}

export function findOpenQueueByEmail(
  email: string,
  items?: RedeemQueueItem[],
  exceptCode = '',
) {
  const key = normalizeAccountKey(email)
  if (!key.includes('@')) return null
  const skip = String(exceptCode || '').trim().toUpperCase()
  const list = items || loadQueue()
  return (
    list.find((item) => {
      if (!isOpenQueueStatus(item.status)) return false
      if (normalizeAccountKey(item.email) !== key) return false
      if (skip && item.code.trim().toUpperCase() === skip) return false
      return true
    }) || null
  )
}

export function preferRedeemStatus(...statuses: string[]) {
  const score = (raw: string) => {
    const s = String(raw || '').toLowerCase()
    if (['completed', 'used', 'success', 'done', 'paid'].includes(s)) return 50
    if (['failed', 'declined', 'cancelled', 'failed_precharge', 'already_satisfied', 'account_busy', 'error', 'void'].includes(s)) return 40
    if (s === 'review' || s === 'pending' || s === 'manual_review' || s === 'reconciling') return 30
    if (s === 'accepted') return 20
    if (['processing', 'queued', 'running', 'reserved', 'requires_action', 'plus_paid', 'funding_pending', 'awaiting_card', 'dispatching'].includes(s)) return 10
    if (s === 'unused') return 1
    if (!s || s === 'unknown') return 0
    return 5
  }
  let best = ''
  let bestScore = -1
  for (const raw of statuses) {
    const s = String(raw || '').toLowerCase()
    const n = score(s)
    if (n > bestScore) {
      best = s
      bestScore = n
    }
  }
  return best
}

export function pickRemoteEmail(...vals: unknown[]) {
  for (const v of vals) {
    const s = String(v || '').trim()
    if (s.includes('@')) return s
  }
  return ''
}

export interface RedeemRemoteSnapshot {
  jobId?: string
  status: string
  email: string
  plan: string
  hasSession: boolean
  message?: string
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null
}

function isGhostPreviewProcessing(remoteSt: string, remote: Record<string, unknown>) {
  if (!['processing', 'queued', 'running', ''].includes(remoteSt)) return false
  const stage = String(remote.stage || '').toLowerCase()
  return !['poll', 'redeem_submit', 'reserved', 'allocating', 'preflight_ok', 'manual_review'].includes(stage)
}

export function mergeRedeemSnapshot(parts: {
  remote?: Record<string, unknown> | null
  lookup?: Record<string, unknown> | null
  local?: Pick<RedeemQueueItem, 'status' | 'email' | 'plan'> | null
}): RedeemRemoteSnapshot {
  const remote = parts.remote || {}
  const order = asRecord(remote.order) || asRecord(remote.data) || remote
  const lookup = parts.lookup || {}
  const jobId = String(remote.job_id || order.job_id || '')
  if (jobId) {
    return {
      jobId,
      status: String(remote.status || order.status || 'processing'),
      email: pickRemoteEmail(remote.account_email, order.account_email, order.email, lookup.account_email, parts.local?.email),
      plan: String(remote.plan || order.plan || lookup.plan || parts.local?.plan || ''),
      hasSession: Boolean(remote.has_session_binding || order.has_session_binding),
      message: String(remote.stage_message || order.stage_message || remote.message || ''),
    }
  }
  const remoteSt = String(order.status || remote.status || '')
  const lookupSt = String(lookup.status || (lookup.used ? 'used' : ''))
  let errHint = String(
    remote.message ||
      order.message ||
      remote.last_error ||
      remote.stage_message ||
      order.stage_message ||
      '',
  )
  if (/captcha|不支持开通|not eligible|ineligible/i.test(errHint) && !/账号异常/.test(errHint)) {
    errHint = ACCOUNT_SWITCH_HINT
  }
  let status = preferRedeemStatus(lookupSt, remoteSt, parts.local?.status || '')
  const localSt = String(parts.local?.status || '').toLowerCase()
  const remoteInFlight =
    remote.processing === true ||
    ['poll', 'redeem_submit', 'reserved', 'allocating', 'preflight_ok', 'manual_review', 'requires_action', 'plus_paid', 'funding_pending', 'awaiting_card', 'dispatching'].includes(
      String(remote.stage || remote.status || '').toLowerCase(),
    )
  const resultMissing = /兑换结果不存在|result not found|轮询超时/i.test(errHint)
  const timeoutReleased = /预检超时/.test(errHint) && remote.processing === false
  if (
    remoteSt === 'failed' ||
    remoteSt === 'already_satisfied' ||
    remoteSt === 'account_busy' ||
    lookupSt === 'failed' ||
    lookupSt === 'already_satisfied' ||
    lookupSt === 'account_busy' ||
    /不支持开通|captcha|更换账号|账号异常|验证码|not eligible|ineligible|到期后才能再次购买|才能再次购买|不能重复购买|当前套餐已满足|进行中的直充|开通中的订单/i.test(
      errHint,
    )
  ) {
    status = 'failed'
  } else if (timeoutReleased) {
    status = 'unused'
  } else if (resultMissing && !['failed', 'declined', 'failed_precharge', 'cancelled'].includes(remoteSt)) {
    status = ['processing', 'accepted', 'queued', 'reserved'].includes(localSt) || remoteInFlight ? 'processing' : 'unused'
  } else if (
    (lookupSt === 'unused' || remoteSt === 'unused') &&
    ['processing', 'accepted', 'queued', 'reserved'].includes(localSt)
  ) {
    const releasedIdle = remote.processing !== true && !remoteInFlight
    status = timeoutReleased || releasedIdle ? 'unused' : 'processing'
  } else if (lookupSt === 'unused' && !localSt && isGhostPreviewProcessing(remoteSt, remote)) {
    status = 'unused'
  }
  return {
    status,
    email: pickRemoteEmail(order.account_email, order.email, remote.account_email, lookup.account_email, parts.local?.email),
    plan: String(order.plan || lookup.plan || parts.local?.plan || ''),
    hasSession: Boolean(remote.has_session_binding || order.has_session_binding),
    message: errHint || undefined,
  }
}

export function applySnapshotToQueue(item: RedeemQueueItem, snap: RedeemRemoteSnapshot) {
  const patch: Partial<RedeemQueueItem> = {}
  const nextStatus = snap.jobId ? mapQueueStatus(snap.status) : resolveQueueWriteStatus(item.status, snap.status)
  if (snap.jobId && snap.jobId !== item.jobId) patch.jobId = snap.jobId
  if (nextStatus && nextStatus !== item.status) patch.status = nextStatus
  if (snap.email && snap.email !== item.email) patch.email = snap.email
  if (snap.plan && snap.plan !== item.plan) patch.plan = snap.plan
  if (snap.hasSession && !item.hasSession) patch.hasSession = true
  if (nextStatus === 'failed' && snap.message) {
    patch.message = snap.message
  }
  if (Object.keys(patch).length === 0) return item
  const list = updateQueue(item.id, patch)
  return list.find((x) => x.id === item.id) || { ...item, ...patch }
}

export function queueAccountLabel(item: Pick<RedeemQueueItem, 'email' | 'hasSession'>, empty = '暂无账号', bound = '已绑定 Session') {
  if (item.email) return item.email
  if (item.hasSession) return bound
  return empty
}

export function openQueueCount(items: RedeemQueueItem[]) {
  return items.filter((item) => isOpenQueueStatus(item.status)).length
}
